import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import JSZip from 'jszip'
import { api } from '../services/api'
import { PageHeader, Card, Button, Modal, Field, Input, EmptyState, ConfirmModal } from '../components/ui'

type TableType = 'DINE_IN' | 'TAKEOUT' | 'ROOM' | 'SELF_CHECKOUT'

const TABLE_TYPE_LABEL: Record<TableType, string> = {
  DINE_IN:       'Establecimiento',
  TAKEOUT:       'Para llevar',
  ROOM:          'Habitación',
  SELF_CHECKOUT: 'Autocobro',
}

interface Table {
  id: string
  number: number
  alias: string | null
  capacity: number
  type: TableType
  qrCode: string
  guestSessionToken: string | null
  waiters: Array<{ waiter: { id: string; name: string } }>
  _count: { orders: number }
}

interface StaffMember {
  id: string
  name: string
  role: string
}

export default function TablesPage() {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ number: '', alias: '', capacity: '', type: 'DINE_IN' as TableType })
  const [editTable, setEditTable] = useState<Table | null>(null)
  const [editForm, setEditForm] = useState({ number: '', alias: '', capacity: '', type: 'DINE_IN' as TableType })
  const [assignTable, setAssignTable] = useState<Table | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Table | null>(null)
  const [transferSource, setTransferSource] = useState<Table | null>(null)
  const [menuAssignTable, setMenuAssignTable] = useState<Table | null>(null)

  const { data: tables = [], isLoading } = useQuery<Table[]>({
    queryKey: ['tables'],
    queryFn: async () => (await api.get('/tables')).data,
  })

  const { data: restaurant } = useQuery<{ venueType: string; eventModeActive: boolean; requireTableActivation: boolean; paymentEnabled?: boolean; paymentGateway?: string | null }>({
    queryKey: ['restaurant-settings'],
    queryFn: async () => (await api.get('/restaurant')).data,
    staleTime: 5 * 60 * 1000,
  })

  const isRoomVenue = new Set(['HOTEL', 'APARTMENT', 'HOSPITAL']).has(restaurant?.venueType ?? '')
  const isEventMode = restaurant?.eventModeActive ?? false
  const requireTableActivation = restaurant?.requireTableActivation ?? false
  const unitLabel = isRoomVenue ? 'Cuarto' : 'Mesa'
  const unitLabelPlural = isRoomVenue ? 'Cuartos' : 'Mesas'

  const { data: staff = [] } = useQuery<StaffMember[]>({
    queryKey: ['staff'],
    queryFn: async () => (await api.get('/restaurant/staff')).data,
  })

  const waiters = staff.filter((s) => s.role === 'WAITER')

  const { mutate: create, isPending } = useMutation({
    mutationFn: async () =>
      api.post('/tables', { number: Number(form.number), alias: form.alias.trim() || null, capacity: Number(form.capacity), type: form.type }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tables'] })
      setOpen(false)
      setForm({ number: '', alias: '', capacity: '', type: 'DINE_IN' })
    },
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: async () =>
      api.patch(`/tables/${editTable!.id}`, {
        number: Number(editForm.number),
        alias: editForm.alias.trim() || null,
        capacity: Number(editForm.capacity),
        type: editForm.type,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tables'] })
      setEditTable(null)
    },
  })

  const { mutate: remove } = useMutation({
    mutationFn: async (id: string) => api.delete(`/tables/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tables'] }),
  })

  const { mutate: assignWaiter } = useMutation({
    mutationFn: async ({ tableId, waiterId }: { tableId: string; waiterId: string }) =>
      api.post(`/tables/${tableId}/assign-waiter`, { waiterId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tables'] }),
  })

  const { mutate: unassignWaiter } = useMutation({
    mutationFn: async ({ tableId, waiterId }: { tableId: string; waiterId: string }) =>
      api.delete(`/tables/${tableId}/assign-waiter/${waiterId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tables'] }),
  })


  const { mutate: grantSession } = useMutation({
    mutationFn: async (tableId: string) => api.post(`/tables/${tableId}/grant-session`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tables'] }),
  })

  const { mutate: revokeSession } = useMutation({
    mutationFn: async (tableId: string) => api.delete(`/tables/${tableId}/session`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tables'] }),
  })

  const [transferError, setTransferError] = useState('')
  const { mutate: transfer, isPending: isTransferring } = useMutation({
    mutationFn: async (targetTableId: string) => {
      const { data } = await api.post(`/tables/${transferSource!.id}/transfer`, { targetTableId })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tables'] })
      setTransferSource(null)
      setTransferError('')
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      setTransferError(e.response?.data?.message ?? 'Error al transferir')
    },
  })

  async function downloadQRImage(tableId: string, tableNumber: number) {
    const response = await api.get(`/tables/${tableId}/qr`, { responseType: 'blob' })
    const url = URL.createObjectURL(response.data as Blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `qr-${tableNumber}.png`
    link.click()
    URL.revokeObjectURL(url)
  }

  const [downloadingAll, setDownloadingAll] = useState(false)

  async function downloadAllQRs() {
    if (tables.length === 0) return
    setDownloadingAll(true)
    try {
      const zip = new JSZip()
      await Promise.all(
        tables.map(async (t) => {
          const response = await api.get(`/tables/${t.id}/qr`, { responseType: 'blob' })
          const label = t.type === 'ROOM' ? 'hab' : unitLabel.toLowerCase()
          zip.file(`${label}-${t.number}.png`, response.data as Blob)
        })
      )
      const blob = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `qr-${unitLabelPlural.toLowerCase()}.zip`
      link.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloadingAll(false)
    }
  }

  // Meseros que aún no están asignados a la mesa seleccionada
  const assignedIds = new Set(assignTable?.waiters.map((w) => w.waiter.id) ?? [])
  const availableWaiters = waiters.filter((w) => !assignedIds.has(w.id))

  return (
    <div className="p-8">
      <PageHeader
        title={unitLabelPlural}
        subtitle={`${tables.length} ${unitLabel.toLowerCase()}(s) configurada(s)`}
        action={
          <div className="flex gap-2">
            {tables.length > 0 && (
              <Button
                variant="secondary"
                loading={downloadingAll}
                onClick={downloadAllQRs}
              >
                ⬇ Descargar todos los QR
              </Button>
            )}
            <Button onClick={() => setOpen(true)}>+ Nuevo {unitLabel.toLowerCase()}</Button>
          </div>
        }
      />

      {isLoading
        ? <div className="text-sm text-gray-400">Cargando…</div>
        : tables.length === 0
          ? <EmptyState emoji="🪑" title={`Sin ${unitLabelPlural.toLowerCase()}`} sub={`Crea tus ${unitLabelPlural.toLowerCase()} para generar los códigos QR`} />
          : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {tables.map((t) => (
                <Card key={t.id} className="p-5 flex flex-col gap-4">
                  {/* QR preview */}
                  {t.qrCode && t.qrCode !== 'pending' && (
                    <img
                      src={t.qrCode}
                      alt={`QR ${unitLabel} ${t.number}`}
                      className="w-full aspect-square object-contain rounded-lg bg-gray-50 p-2"
                    />
                  )}

                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xl font-black text-gray-900">
                        {t.alias ?? `${t.type === 'ROOM' ? 'Hab.' : unitLabel} ${t.number}`}
                      </p>
                      {t.alias && <p className="text-xs text-gray-400">#{t.number}</p>}
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        t.type === 'TAKEOUT'
                          ? 'bg-blue-50 text-blue-600'
                          : t.type === 'ROOM'
                          ? 'bg-purple-50 text-purple-700'
                          : 'bg-green-50 text-green-700'
                      }`}>
                        {TABLE_TYPE_LABEL[t.type] ?? t.type}
                      </span>
                      {requireTableActivation && (
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${
                          t.guestSessionToken
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${t.guestSessionToken ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                          {t.guestSessionToken ? 'Activa' : 'Inactiva'}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">Capacidad: {t.capacity}</p>

                    {/* Meseros asignados */}
                    {t.waiters.length > 0 ? (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {t.waiters.map((w) => (
                          <span
                            key={w.waiter.id}
                            className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full"
                          >
                            {w.waiter.name}
                            <button
                              className="text-gray-400 hover:text-red-500 leading-none"
                              onClick={() => unassignWaiter({ tableId: t.id, waiterId: w.waiter.id })}
                              title="Quitar mesero"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 mt-1">Sin mesero asignado</p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {requireTableActivation && (
                      t.guestSessionToken ? (
                        <Button
                          size="sm"
                          variant="danger"
                          className="w-full justify-center"
                          onClick={() => revokeSession(t.id)}
                        >
                          🔒 Desactivar mesa
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          className="w-full justify-center"
                          onClick={() => grantSession(t.id)}
                        >
                          🔓 Activar mesa
                        </Button>
                      )
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      className="w-full justify-center"
                      onClick={() => setAssignTable(t)}
                    >
                      Asignar mesero
                    </Button>
                    {isEventMode && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-full justify-center"
                        onClick={() => setMenuAssignTable(t)}
                      >
                        🍽 Menús del salón
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      className="w-full justify-center"
                      onClick={() => {
                        setEditTable(t)
                        setEditForm({ number: String(t.number), alias: t.alias ?? '', capacity: String(t.capacity), type: t.type })
                      }}
                    >
                      Editar
                    </Button>
                    {t._count.orders > 0 && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-full justify-center"
                        onClick={() => { setTransferSource(t); setTransferError('') }}
                      >
                        ↔ Transferir órdenes
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      className="w-full justify-center"
                      onClick={() => downloadQRImage(t.id, t.number)}
                    >
                      ⬇ Descargar QR
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      className="w-full justify-center"
                      onClick={() => setConfirmDelete(t)}
                    >
                      Eliminar
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )
      }


      <ConfirmModal
        open={!!confirmDelete}
        title={`¿Eliminar ${confirmDelete?.type === 'ROOM' ? 'Habitación' : unitLabel} ${confirmDelete?.number}?`}
        message={`Se eliminará el ${unitLabel.toLowerCase()} y su código QR. Los comensales que intenten escanear el QR recibirán un error.`}
        warning="Esta acción no se puede deshacer."
        onConfirm={() => { remove(confirmDelete!.id); setConfirmDelete(null) }}
        onCancel={() => setConfirmDelete(null)}
      />

      {/* Modal: transferir órdenes */}
      {transferSource && (
        <Modal
          title={`Transferir órdenes — ${unitLabel} ${transferSource.number}`}
          open
          onClose={() => { setTransferSource(null); setTransferError('') }}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Se moverán <span className="font-semibold text-gray-900">{transferSource._count.orders} orden(es) activa(s)</span> a la {unitLabel.toLowerCase()} destino.
              La {unitLabel.toLowerCase()} destino debe estar libre (sin órdenes activas).
            </p>

            <div className="space-y-2">
              {tables
                .filter((t) => t.id !== transferSource.id && t._count.orders === 0)
                .map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    disabled={isTransferring}
                    onClick={() => transfer(t.id)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 hover:border-gray-900 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    <span className="font-medium text-gray-900">
                      {t.type === 'ROOM' ? 'Hab.' : unitLabel} {t.number}
                    </span>
                    <span className="text-xs text-green-600 font-semibold">Disponible</span>
                  </button>
                ))}
              {tables.filter((t) => t.id !== transferSource.id && t._count.orders === 0).length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">
                  No hay {unitLabelPlural.toLowerCase()} disponibles para transferir.
                </p>
              )}
            </div>

            {transferError && <p className="text-sm text-red-500">{transferError}</p>}

            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => { setTransferSource(null); setTransferError('') }}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: nueva mesa */}
      <Modal title={`Nuevo ${unitLabel.toLowerCase()}`} open={open} onClose={() => setOpen(false)}>
        <div className="space-y-4">
          <Field label={`Número de ${unitLabel.toLowerCase()}`}>
            <Input
              type="number" min="1"
              value={form.number}
              onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))}
              placeholder="1"
            />
          </Field>
          <Field label="Alias (opcional)">
            <Input
              value={form.alias}
              onChange={(e) => setForm((f) => ({ ...f, alias: e.target.value }))}
              placeholder="Ej: VIP, Terraza 1, Barra..."
              maxLength={60}
            />
          </Field>
          <Field label="Capacidad (personas)">
            <Input
              type="number" min="1"
              value={form.capacity}
              onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
              placeholder="4"
            />
          </Field>
          <Field label={`Tipo de ${unitLabel.toLowerCase()}`}>
            <div className="flex flex-wrap gap-2">
              {(['DINE_IN', 'TAKEOUT', 'ROOM', 'SELF_CHECKOUT'] as TableType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, type: t }))}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    form.type === t
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  {TABLE_TYPE_LABEL[t]}
                </button>
              ))}
            </div>
            {form.type === 'SELF_CHECKOUT' && !restaurant?.paymentEnabled && (
              <p className="mt-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                ⚠️ Debes configurar y habilitar una pasarela de pago en Configuración antes de crear una mesa de Autocobro.
              </p>
            )}
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button
              loading={isPending}
              disabled={!form.number || !form.capacity || (form.type === 'SELF_CHECKOUT' && !restaurant?.paymentEnabled)}
              onClick={() => create()}
            >
              Crear y generar QR
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal: editar mesa */}
      <Modal title={`Editar ${unitLabel} ${editTable?.number}`} open={!!editTable} onClose={() => setEditTable(null)}>
        <div className="space-y-4">
          <Field label={`Número de ${unitLabel.toLowerCase()}`}>
            <Input
              type="number" min="1"
              value={editForm.number}
              onChange={(e) => setEditForm((f) => ({ ...f, number: e.target.value }))}
            />
          </Field>
          <Field label="Alias (opcional)">
            <Input
              value={editForm.alias}
              onChange={(e) => setEditForm((f) => ({ ...f, alias: e.target.value }))}
              placeholder="Ej: VIP, Terraza 1, Barra..."
              maxLength={60}
            />
          </Field>
          <Field label="Capacidad (personas)">
            <Input
              type="number" min="1"
              value={editForm.capacity}
              onChange={(e) => setEditForm((f) => ({ ...f, capacity: e.target.value }))}
            />
          </Field>
          <Field label={`Tipo de ${unitLabel.toLowerCase()}`}>
            <div className="flex flex-wrap gap-2">
              {(['DINE_IN', 'TAKEOUT', 'ROOM', 'SELF_CHECKOUT'] as TableType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setEditForm((f) => ({ ...f, type: t }))}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    editForm.type === t
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  {TABLE_TYPE_LABEL[t]}
                </button>
              ))}
            </div>
            {editForm.type === 'SELF_CHECKOUT' && !restaurant?.paymentEnabled && (
              <p className="mt-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                ⚠️ Debes configurar y habilitar una pasarela de pago en Configuración antes de usar Autocobro.
              </p>
            )}
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditTable(null)}>Cancelar</Button>
            <Button
              loading={isUpdating}
              disabled={!editForm.number || !editForm.capacity}
              onClick={() => update()}
            >
              Guardar cambios
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal: asignar mesero a mesa */}
      <Modal
        title={assignTable ? `Meseros — ${unitLabel} ${assignTable.number}` : ''}
        open={!!assignTable}
        onClose={() => setAssignTable(null)}
      >
        {assignTable && (
          <div className="space-y-4">
            {/* Meseros ya asignados */}
            {assignTable.waiters.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Asignados
                </p>
                <div className="flex flex-col gap-2">
                  {assignTable.waiters.map((w) => (
                    <div
                      key={w.waiter.id}
                      className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                    >
                      <span className="text-sm font-medium text-gray-800">{w.waiter.name}</span>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => {
                          unassignWaiter(
                            { tableId: assignTable.id, waiterId: w.waiter.id },
                            {
                              onSuccess: () =>
                                setAssignTable((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        waiters: prev.waiters.filter(
                                          (x) => x.waiter.id !== w.waiter.id
                                        ),
                                      }
                                    : null
                                ),
                            }
                          )
                        }}
                      >
                        Quitar
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Meseros disponibles para asignar */}
            {availableWaiters.length > 0 ? (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Disponibles
                </p>
                <div className="flex flex-col gap-2">
                  {availableWaiters.map((w) => (
                    <div
                      key={w.id}
                      className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2"
                    >
                      <span className="text-sm text-gray-800">{w.name}</span>
                      <Button
                        size="sm"
                        onClick={() => {
                          assignWaiter(
                            { tableId: assignTable.id, waiterId: w.id },
                            {
                              onSuccess: () =>
                                setAssignTable((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        waiters: [
                                          ...prev.waiters,
                                          { waiter: { id: w.id, name: w.name } },
                                        ],
                                      }
                                    : null
                                ),
                            }
                          )
                        }}
                      >
                        Asignar
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              assignTable.waiters.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">
                  No hay meseros registrados. Créalos en la sección Personal.
                </p>
              )
            )}

            <div className="flex justify-end pt-2">
              <Button variant="secondary" onClick={() => setAssignTable(null)}>
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal: asignar menús a mesa (modo evento) */}
      {menuAssignTable && (
        <TableMenusModal
          table={menuAssignTable}
          unitLabel={unitLabel}
          onClose={() => setMenuAssignTable(null)}
        />
      )}
    </div>
  )
}

// ─── TableMenusModal ──────────────────────────────────────────────────────────
interface MenuOption { id: string; name: string; active: boolean }

function TableMenusModal({
  table,
  unitLabel,
  onClose,
}: {
  table: Table
  unitLabel: string
  onClose: () => void
}) {
  const { data: menus = [], isLoading: menusLoading } = useQuery<MenuOption[]>({
    queryKey: ['menus-list'],
    queryFn: async () => (await api.get('/menus')).data,
    staleTime: 60_000,
  })

  const { data: assigned = [], isLoading: assignedLoading } = useQuery<string[]>({
    queryKey: ['table-menus', table.id],
    queryFn: async () => (await api.get(`/tables/${table.id}/menus`)).data,
  })

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [initialized, setInitialized] = useState(false)

  if (!initialized && !assignedLoading && assigned.length >= 0) {
    setSelected(new Set(assigned))
    setInitialized(true)
  }

  const { mutate: save, isPending } = useMutation({
    mutationFn: async () => {
      await api.put(`/tables/${table.id}/menus`, { menuIds: Array.from(selected) })
    },
    onSuccess: onClose,
  })

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const isLoading = menusLoading || assignedLoading || !initialized

  return (
    <Modal
      title={`Menús — ${unitLabel} ${table.number}`}
      open
      onClose={onClose}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Selecciona los menús que verán los comensales de este salón. Si no seleccionas ninguno, verán todos los menús activos.
        </p>

        {isLoading ? (
          <p className="text-sm text-gray-400 py-4 text-center">Cargando…</p>
        ) : menus.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">No hay menús creados aún.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {menus.map((m) => (
              <label
                key={m.id}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${
                  selected.has(m.id)
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(m.id)}
                  onChange={() => toggle(m.id)}
                  className="w-4 h-4 accent-gray-900"
                />
                <span className="text-sm font-medium text-gray-900">{m.name}</span>
                {!m.active && (
                  <span className="ml-auto text-xs text-gray-400">Inactivo</span>
                )}
              </label>
            ))}
          </div>
        )}

        {selected.size === 0 && !isLoading && (
          <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
            Sin selección: este salón verá todos los menús activos del negocio.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button loading={isPending} disabled={isLoading} onClick={() => save()}>
            Guardar
          </Button>
        </div>
      </div>
    </Modal>
  )
}
